<template>
  <b-container
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end mb-3 pb-0">
      <b-breadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <b-card>
      <b-row class="mb-2">
        <b-col
          cols="12"
        >
          <div class="form-group">
            <label>
              <strong>Search</strong>
            </label>
            <b-input
              v-model="tableCompanies.filter.search"
              type="text"
              placeholder="search here"
              debounce="1000"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
        >
          <b-button
            type="button"
            variant="success"
            autocomplete="off"
            @click="onCreate"
          >
            Add Company
          </b-button>
        </b-col>

      </b-row>
      <b-table
        ref="tableCompanies"
        hover
        responsive
        class="mt-2"
        :per-page="tableCompanies.perPage"
        :current-page="tableCompanies.currentPage"
        :items="tableProvider"
        :fields="tableCompanies.fields"
        :sort-by.sync="tableCompanies.sortBy"
        :sort-desc.sync="tableCompanies.sortDesc"
        :sort-direction="tableCompanies.sortDirection"
        :filter="tableCompanies.filter"
        :filter-included-fields="tableCompanies.filterOn"
        show-empty
      >
        <template #cell(index)="data">
          {{ tableCompanies.currentPage * tableCompanies.perPage - tableCompanies.perPage + (data.index + 1) }}
        </template>

        <template #cell(active)="data">
          {{ data.item.active == 1 ? 'Yes' : 'No' }}
        </template>

        <template #cell()="data">
          <div class="text-nowrap">
            {{ data.value }}
          </div>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            @click="onEdit(row.item)"
          >
            Edit
          </b-button>
        </template>

      </b-table>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <div class="w-100 w-sm-25 mb-2 sm-mb-2">
            <b-select
              v-model="tableCompanies.perPage"
              :options="tableCompanies.pageOptions"
              size="sm"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="tableCompanies.currentPage"
            :total-rows="tableCompanies.totalRows"
            :per-page="tableCompanies.perPage"
            first-number
            last-number
            pills
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="modal-company-form"
      scrollable
      no-close-on-backdrop
      no-enforce-focus
      aria-hidden="false"
      :title="modalTitle"
      @ok="onValidate"
    >
      <ValidationObserver
        ref="formCompany"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <ValidationProvider
            #default="{ errors }"
            name="company code"
            vid="company_code"
            rules="required|max:13"
          >
            <b-form-group>
              <label for="company_code">
                <strong>Code</strong>
              </label>
              <b-input
                id="company_code"
                v-model="company.company_code"
                type="text"
                placeholder="enter company code"
                autocomplete="off"
                maxlength="13"
                :state="errors.length > 0 ? false : null"
                :disabled="state.busy || state.editing"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            #default="{ errors }"
            name="company name"
            vid="company_name"
            rules="required|max:150"
          >
            <b-form-group>
              <label for="company_name">
                <strong>Name</strong>
              </label>
              <b-input
                id="company_name"
                v-model="company.company_name"
                type="text"
                placeholder="enter company name"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
                :disabled="state.busy"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <div
            v-if="state.editing"
            class="form-group"
          >
            <b-form-checkbox
              id="is_active"
              v-model="company.active"
              name="is_active"
              value="1"
              unchecked-value="0"
              switch
              :disabled="state.busy"
            >
              Is Active?
            </b-form-checkbox>
          </div>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ok, cancel}">
        <b-button
          variant="success"
          @click="ok()"
        >
          {{ state.editing ? 'Update Record' : 'Save Record' }}
        </b-button>
        <b-button
          variant="outline-dark"
          @click="cancel()"
        >
          Close Window
        </b-button>
      </template>

    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import { core } from '@/config/pluginInit'
import { AxiosError, SAdminCompany } from '@/services'
import formatter from '@/mixins/formatter'

export default {
  name: 'VAdminCompanies',

  middleware: ['auth', 'admin'],

  metaInfo () {
    return {
      title: 'Companies'
    }
  },

  mixins: [formatter],

  data () {
    return {
      state: {
        editing: false,
        busy: false
      },
      company: {
        id: 0,
        company_code: '',
        company_name: '',
        active: 0
      },
      tableCompanies: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: ''
        },
        filterOn: [],
        fields: [
          'index',
          'actions',
          { key: 'created_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'company_code', sortable: true },
          { key: 'company_name', sortable: true },
          { key: 'active' },
          { key: 'updated_at', formatter: this.dateTimeFormatter, sortable: true }
        ]
      }
    }
  },

  computed: {
    modalTitle () {
      return this.state.editing ? 'Edit Company' : 'New Company'
    }
  },

  mounted () {
    core.index()
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableCompanies.busy = true
      const filters = this.objectToUrl({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      })

      return await SAdminCompany.get(filters).then(
        ({ data }) => {
          this.tableCompanies.totalRows = data.total_rows
          return data.items
        })
        .catch(() => {
          this.tableCompanies.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableCompanies.busy = false
        })
    },

    onCreate () {
      this.state.editing = false
      this.company.company_code = ''
      this.company.company_name = ''
      this.company.active = 1
      this.$bvModal.show('modal-company-form')
    },

    onEdit (item) {
      this.state.editing = true
      this.company.id = item.id
      this.company.company_code = item.company_code
      this.company.company_name = item.company_name
      this.company.active = item.active
      this.$bvModal.show('modal-company-form')
    },

    async onValidate (bvModalEvt) {
      bvModalEvt.preventDefault()
      await this.$refs.formCompany.validate().then(
        async areAllFieldsValid => {
          if (areAllFieldsValid) {
            this.$swal.fire({
              icon: 'question',
              title: 'Confirmation',
              text: this.state.editing ? 'Update Company?' : 'Create Company?',
              confirmButtonColor: '#06C270',
              confirmButtonText: 'Confirm',
              cancelButtonColor: '#737373',
              showCancelButton: true,
              showLoaderOnConfirm: true,
              preConfirm: () => {
                this.state.busy = true
                if (this.state.editing) {
                  return this.onPut()
                }

                return this.onPost()
              },
              allowOutsideClick: () => !this.$swal.isLoading()
            })
          } else {
            this.$bvModal.msgBoxOk('Oops! There were problem with your inputs.', {
              title: 'Validation Error',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              centered: true
            })
          }
        }
      )
    },

    async onPost () {
      return new Promise(
        resolve => {
          SAdminCompany.post(this.company).then(
            ({ data }) => {
              this.$bvModal.hide('modal-company-form')
              this.$swal.fire({
                icon: 'success',
                title: 'Successful',
                text: data.message,
                confirmButtonColor: '#06C270',
                confirmButtonText: 'Dismiss'
              }).then(() => {
                this.$refs.tableCompanies.refresh()
              })
            }
          ).catch(
            error => {
              if (error instanceof AxiosError) {
                if (error.code === 422) {
                  this.$refs.formCompany.setErrors(error.message)
                }
              }
            }
          ).finally(resolve)
        }
      ).then(() => {
        this.state.busy = false
      })
    },

    async onPut () {
      return new Promise(
        resolve => {
          SAdminCompany.put(this.company).then(
            ({ data }) => {
              this.$bvModal.hide('modal-company-form')
              this.$swal.fire({
                icon: 'success',
                title: 'Successful',
                text: data.message,
                confirmButtonColor: '#06C270',
                confirmButtonText: 'Dismiss'
              }).then(() => {
                const row = _.find(this.$refs.tableCompanies.localItems, {
                  id: data.company.id
                })
                row.company_code = data.company.company_code
                row.company_name = data.company.company_name
                row.active = data.company.active
                row.updated_at = data.company.updated_at
              })
            }
          ).catch(
            error => {
              if (error instanceof AxiosError) {
                if (error.code === 422) {
                  this.$refs.formCompany.setErrors(error.message)
                }
              }
            }
          ).finally(resolve)
        }
      ).then(() => {
        this.state.busy = false
      })
    }
  }
}
</script>
